import { useCallback, useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge, Button, Select, Alert } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import ToolProviderCreateDialog, { KIND_LABELS } from '@/components/ToolProviderCreateDialog';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

function KindBadge({ kind }) {
  return <Badge variant="primary" size="sm">{KIND_LABELS[kind]?.label || kind}</Badge>;
}

/** Unified status: covers both ToolProvidersPage and McpServersPage variants. */
function StatusBadge({ provider }) {
  if (!provider.is_active) return <Badge variant="secondary" size="sm">inactive</Badge>;
  if (provider.status === 'drifted') return <Badge variant="warning" size="sm">drifted — re-approve</Badge>;
  if (provider.status === 'error') return <Badge variant="error" size="sm">unreachable</Badge>;
  if (provider.status === 'ok') return <Badge variant="success" size="sm">connected</Badge>;
  return <Badge variant="secondary" size="sm">{provider.status || 'not synced yet'}</Badge>;
}

function ToolsCell({ provider }) {
  const total = provider.tool_count || 0;
  const armed = provider.armed_count || 0;
  const embedded = provider.embedded_count || 0;
  // MCP servers routinely differ total vs armed; for http/client show armed when present
  if (provider.kind === 'mcp') {
    if (!total) return <span className="text-xs text-secondary-500">none discovered</span>;
    return (
      <span className="text-xs">
        {total} discovered
        {armed ? <span className="text-secondary-500"> · {armed} armed</span>
          : <span className="text-amber-600"> · none armed</span>}
        {armed > 0 && <span className="text-secondary-500"> · {embedded} indexed</span>}
      </span>
    );
  }
  if (!total) return <span className="text-xs text-secondary-500">0</span>;
  if (armed && armed !== total) return <span className="text-xs">{total} · <span className="text-secondary-500">{armed} armed</span></span>;
  return <span className="text-xs">{total}</span>;
}

const COLUMNS = [
  {
    key: 'name',
    label: 'Provider',
    render: (p) => (
      <div>
        <div className="font-medium text-primary-600 flex items-center gap-1.5">
          {p.name}
          {/* A platform row is ONE row shared by every company, so the server refuses any
              write to it (403). Saying so here is the difference between "read-only by
              design" and "the toggle is broken". */}
          {p.is_platform && <Badge variant="secondary" size="sm">platform</Badge>}
        </div>
        <div className="text-secondary-500 text-xs font-mono">
          {/* Platform tools bind under their plain registry name, not a namespaced one. */}
          {p.is_platform ? 'built-in names' : `${p.slug}_*`}
        </div>
      </div>
    ),
  },
  { key: 'kind', label: 'Runs on', searchable: false, render: (p) => <KindBadge kind={p.kind} /> },
  { key: 'base_url', label: 'Endpoint', render: (p) => p.base_url ? <span className="text-xs break-all">{p.base_url}</span> : '—' },
  { key: 'tool_count', label: 'Tools', searchable: false, render: (p) => <ToolsCell provider={p} /> },
  { key: 'status', label: 'Status', searchable: false, render: (p) => <StatusBadge provider={p} /> },
];

const KIND_TABS = [
  { value: 'all', label: 'All' },
  { value: 'http', label: 'HTTP API' },
  { value: 'mcp', label: 'MCP Servers' },
  { value: 'client', label: 'Browser' },
];

const VALID_KINDS = new Set(['http', 'mcp', 'client']);

export default function ToolProvidersPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKind = searchParams.get('kind') && VALID_KINDS.has(searchParams.get('kind')) ? searchParams.get('kind') : 'all';
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [creating, setCreating] = useState(false);
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('toolProvidersList')
      .then((res) => setProviders(Array.isArray(res) ? res : []))
      .catch((ex) => setErr(ex?.response?.data?.error || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, []);

  // Keep URL in sync when user switches tabs (supports bookmarking ?kind=mcp)
  const handleKindChange = (kind) => {
    if (kind === 'all') {
      searchParams.delete('kind');
    } else {
      searchParams.set('kind', kind);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const counts = useMemo(() => {
    const c = { all: providers.length, http: 0, mcp: 0, client: 0 };
    for (const p of providers) if (c[p.kind] != null) c[p.kind]++;
    return c;
  }, [providers]);

  const filteredByKind = activeKind === 'all' ? providers : providers.filter((p) => p.kind === activeKind);
  const filteredProviders = applicationId
    ? filteredByKind.filter((p) => p.application_id == null || p.application_id === Number(applicationId))
    : filteredByKind;

  const unarmedMcp = useMemo(() => providers.filter((p) => p.kind === 'mcp' && (p.tool_count || 0) > 0 && !(p.armed_count || 0)), [providers]);

  const createKindPreset = activeKind !== 'all' && VALID_KINDS.has(activeKind) ? activeKind : undefined;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Tool Providers"
        subtitle={(
          <>
            Tools your customers supply — their REST API, remote MCP servers, or handlers in their browser page.
            Attach providers to agents so agents can use their tools.
          </>
        )}
        actions={(
          <Can module="toolproviders" action="author">
            <Button size="sm" onClick={() => setCreating(true)}>New provider</Button>
          </Can>
        )}
      />

      <div className="flex flex-wrap gap-1">
        {KIND_TABS.map((tab) => (
          <button
            key={tab.value}
            onClick={() => handleKindChange(tab.value)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border ${activeKind === tab.value ? 'bg-primary-600 text-white border-primary-600' : 'border-[var(--app-border)] text-secondary-600 hover:bg-[var(--app-border)]/20'}`}
          >
            {tab.label} <span className={activeKind === tab.value ? 'text-white/80' : 'text-secondary-500'}>({counts[tab.value] || 0})</span>
          </button>
        ))}
      </div>

      {activeKind === 'all' && unarmedMcp.length > 0 && (
        <Alert variant="warning" title="Connected, but contributing nothing">
          {unarmedMcp.map((p) => p.name).join(', ')} — every discovered tool is still waiting for
          approval, so agents attached to {unarmedMcp.length > 1 ? 'these servers' : 'this server'} get
          no tools from {unarmedMcp.length > 1 ? 'them' : 'it'}. Open the provider and use “Approve &amp; enable all”.
        </Alert>
      )}

      <DataTable
        data={filteredProviders}
        columns={COLUMNS}
        loading={loading}
        error={err}
        emptyText={activeKind === 'all' ? 'No tool providers yet.' : `No ${KIND_LABELS[activeKind]?.label || activeKind} providers yet.`}
        search={{ placeholder: 'Filter providers…', keys: ['name', 'slug', 'base_url'] }}
        onRowClick={(p) => nav(`/app-tool-provider/${p.id}`)}
        onReload={reload}
        toolbar={(
          <div className="w-48 sm:w-56">
            <Select options={[{ label: 'All applications', value: '' }, ...apps.map((a) => ({ label: a.name, value: String(a.id) }))]} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable />
          </div>
        )}
      />

      <ToolProviderCreateDialog
        isOpen={creating}
        kindPreset={createKindPreset}
        onClose={(payload) => {
          setCreating(false);
          // For MCP servers, go straight to the detail to arm tools (like old /mcp-server flow)
          if ((payload?.kind === 'mcp' || payload?.kind === 'a2a') && payload?.id) nav(`/app-tool-provider/${payload.id}`);
          else reload();
        }}
        onCreated={reload}
      />
    </PageShell>
  );
}
